import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { GENDER } from 'src/app/constants/health-history.constants';
import { MASK_CONSTANTS } from 'src/app/constants/mask.constants';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { ProfileService } from 'src/app/services/core/profile.service';
import { MedicalHistoryService } from 'src/app/services/ha-new/medical-history.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

/**
 * Screenings component
 */
@Component({
	selector: 'app-screening',
	templateUrl: './screening.component.html',
	styleUrls: ['./screening.component.scss'],
})
export class ScreeningComponent implements OnInit, AfterViewChecked, OnDestroy {
	/** holds screenings form */
	screeningsForm: FormGroup;
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds appointment id */
	apptID: any;
	/** holds readOnly flag */
	readOnly: boolean = false;
	/** holds ha id */
	healthAssessmentID: number;
	/** masks year number in format yyyy */
	yearMask = MASK_CONSTANTS.YEAR;
	/** holds scroll variable */
	scrollTo = '';
	/** holds vaccinations form */
	vaccinationsForm: FormGroup;
	/** holds next route */
	routeTo: string;
	/** Analytics bean */
	analyticsData: AnalyticsBean;
	returningUser: boolean = false;

	/**
	 * constructor
	 * @param medicalHistoryService
	 * @param spinner
	 * @param activatedRoute
	 * @param router
	 * @param haService
	 */
	constructor(
		private medicalHistoryService: MedicalHistoryService,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private haService: NewHAService,
		private profileService: ProfileService
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.healthAssessmentID = params.healthAssessmentID;
			this.returningUser = params.returningUser === 'true';
			if (this.scrollTo != 'false') this.scrollTo = params.section;
		});
		console.log(this.profileService.getGender().value);
		if (this.profileService.getGender().value === GENDER.MALE) {
			this.routeTo = 'men-health';
		} else if (this.profileService.getGender().value === GENDER.FEMALE) {
			this.routeTo = 'women-health';
		} else {
			this.routeTo = 'family-history';
		}
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Screening';
		this.analyticsData.pageUrl = '/ha/new/(medical:screening)';
	}

	/**
	 * on component initialization
	 */
	ngOnInit() {
		this.initializeScreeningsForm();
		this.initializeVaccinationsForm();
		this.presetValues();
	}

	/**
	 * initializes screenings form
	 */
	initializeScreeningsForm() {
		this.screeningsForm = new FormGroup({
			hadPapTest: new FormControl({ value: '', disabled: this.readOnly }),
			yearOfPapTest: new FormControl({ value: '', disabled: this.readOnly }),
			hadMammography: new FormControl({ value: '', disabled: this.readOnly }),
			yearOfMammography: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
			hadCst: new FormControl({ value: '', disabled: this.readOnly }),
			yearOfCst: new FormControl({ value: '', disabled: this.readOnly }),
			hadColonoscopy: new FormControl({ value: '', disabled: this.readOnly }),
			yearOfColonoscopy: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
			hadDexa: new FormControl({ value: '', disabled: this.readOnly }),
			yearOfDexa: new FormControl({ value: '', disabled: this.readOnly }),
		});
	}

	/** initialize vaccinations form */
	initializeVaccinationsForm() {
		this.vaccinationsForm = new FormGroup({
			hadFlu: new FormControl({ value: '', disabled: this.readOnly }),
			dateOfLastFlu: new FormControl({ value: '', disabled: this.readOnly }),
			hadHepA: new FormControl({ value: '', disabled: this.readOnly }),
			hadHepB: new FormControl({ value: '', disabled: this.readOnly }),
			hadHpv: new FormControl({ value: '', disabled: this.readOnly }),
			hadMmr: new FormControl({ value: '', disabled: this.readOnly }),
			hadPneumonia: new FormControl({ value: '', disabled: this.readOnly }),
			hadShingles: new FormControl({ value: '', disabled: this.readOnly }),
			hadTetanus: new FormControl({ value: '', disabled: this.readOnly }),
			yearLastTetanus: new FormControl({ value: '', disabled: this.readOnly }),
			hadTdap: new FormControl({ value: '', disabled: this.readOnly }),
			yearLastTdap: new FormControl({ value: '', disabled: this.readOnly }),
			everHadChickenpox: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
			hadChickenpoxVaccine: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
		});
	}

	ngAfterViewChecked() {
		if (this.scrollTo != 'false') {
			if (this.haService.scrollView(this.scrollTo)) {
				this.scrollTo = 'false';
			}
		}
	}

	/**
	 * presets values in form
	 */
	presetValues() {
		this.getScreenings();
		this.getVaccinations();
	}

	/**
	 * get screenings values
	 */
	getScreenings() {
		this.spinner.startLoader();
		this.medicalHistoryService.getScreenings(this.apptID, this.healthAssessmentID).subscribe(
			res => {
				this.spinner.stopLoader();
				console.log(res);
				this.screeningsForm.controls.hadPapTest.setValue(res.data.hadPapTest);
				this.screeningsForm.controls.yearOfPapTest.setValue(res.data.yearOfPapTest);
				this.screeningsForm.controls.hadMammography.setValue(res.data.hadMammography);
				this.screeningsForm.controls.yearOfMammography.setValue(res.data.yearOfMammography);
				this.screeningsForm.controls.hadCst.setValue(res.data.hadCst);
				this.screeningsForm.controls.yearOfCst.setValue(res.data.yearOfCst);
				this.screeningsForm.controls.hadColonoscopy.setValue(res.data.hadColonoscopy);
				this.screeningsForm.controls.yearOfColonoscopy.setValue(res.data.yearOfColonoscopy);
				this.screeningsForm.controls.hadDexa.setValue(res.data.hadDexa);
				this.screeningsForm.controls.yearOfDexa.setValue(res.data.yearOfDexa);
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${ScreeningComponent.name}][${this.getScreenings.name}]`, 'Error occurred while getting screenings ', err);
			}
		);
	}

	/**
	 * get vaccinations values
	 */
	getVaccinations() {
		this.spinner.startLoader();
		this.medicalHistoryService.getVaccinations(this.apptID, this.healthAssessmentID).subscribe(
			res => {
				this.spinner.stopLoader();
				console.log(res);
				this.vaccinationsForm.controls.hadFlu.setValue(res.data.hadFlu);
				this.vaccinationsForm.controls.dateOfLastFlu.setValue(res.data.dateOfLastFlu);
				this.vaccinationsForm.controls.hadHepA.setValue(res.data.hadHepA);
				this.vaccinationsForm.controls.hadHepB.setValue(res.data.hadHepB);
				this.vaccinationsForm.controls.hadHpv.setValue(res.data.hadHpv);
				this.vaccinationsForm.controls.hadMmr.setValue(res.data.hadMmr);
				this.vaccinationsForm.controls.hadPneumonia.setValue(res.data.hadPneumonia);
				this.vaccinationsForm.controls.hadShingles.setValue(res.data.hadShingles);
				this.vaccinationsForm.controls.hadTetanus.setValue(res.data.hadTetanus);
				this.vaccinationsForm.controls.yearLastTetanus.setValue(res.data.yearLastTetanus);
				this.vaccinationsForm.controls.hadTdap.setValue(res.data.hadTdap);
				this.vaccinationsForm.controls.yearLastTdap.setValue(res.data.yearLastTdap);
				this.vaccinationsForm.controls.hadChickenpoxVaccine.setValue(res.data.hadChickenpoxVaccine);
				this.vaccinationsForm.controls.everHadChickenpox.setValue(res.data.everHadChickenpox);
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${ScreeningComponent.name}][${this.getVaccinations.name}]`, 'Error occurred while getting vaccinations ', err);
			}
		);
	}

	/**
	 * on next
	 */
	next() {
		if (!this.readOnly) {
			this.analyticsData.id = 'HA Medical Info - Screening';
			this.analyticsData.redirectedTo = `/ha/new/(medical:${this.routeTo})`;
			this.analyticsData.placement = 'ha medical info - screenings screen';
			console.log(this.screeningsForm.getRawValue(), this.screeningsForm.valid, this.screeningsForm.touched);
			if (this.screeningsForm.touched) {
				this.saveScreenings();
			}
			if (this.vaccinationsForm.touched) {
				this.saveVaccinations();
			}

			console.log(this.routeTo);
			this.haService.redirect(
				[],
				[this.routeTo],
				[],
				this.apptID,
				this.healthAssessmentID,
				this.readOnly,
				this.profileService.getGender().value === 'MALE' ? 'medical-men-health' : 'medical-women-health',
				this.returningUser
			);
		}
	}

	/**
	 * save screenings
	 */
	saveScreenings() {
		if (!this.screeningsForm.controls.hadPapTest.value) {
			this.screeningsForm.controls.yearOfPapTest.setValue('');
		}
		if (!this.screeningsForm.controls.hadMammography.value) {
			this.screeningsForm.controls.yearOfMammography.setValue('');
		}
		if (!this.screeningsForm.controls.hadCst.value) {
			this.screeningsForm.controls.yearOfCst.setValue('');
		}
		if (!this.screeningsForm.controls.hadColonoscopy.value) {
			this.screeningsForm.controls.yearOfColonoscopy.setValue('');
		}
		if (!this.screeningsForm.controls.hadDexa.value) {
			this.screeningsForm.controls.yearOfDexa.setValue('');
		}
		this.spinner.startLoader();
		this.medicalHistoryService
			.saveScreenings(this.apptID, this.healthAssessmentID, this.screeningsForm.getRawValue())
			.subscribe(
				res => {
					this.spinner.stopLoader();
					console.log(res);
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${ScreeningComponent.name}][${this.saveScreenings.name}]`, 'Error occurred while saving screenings', err);
				}
			);
	}

	/**
	 * save vaccinations
	 */
	saveVaccinations() {
		if (!this.vaccinationsForm.controls.hadFlu.value) {
			this.vaccinationsForm.controls.dateOfLastFlu.setValue('');
		}
		if (!this.vaccinationsForm.controls.hadTetanus.value) {
			this.vaccinationsForm.controls.yearLastTetanus.setValue('');
		}
		if (!this.vaccinationsForm.controls.hadTdap.value) {
			this.vaccinationsForm.controls.yearLastTdap.setValue('');
		}
		this.spinner.startLoader();
		this.medicalHistoryService
			.saveVaccinations(this.apptID, this.healthAssessmentID, this.vaccinationsForm.getRawValue())
			.subscribe(
				res => {
					this.spinner.stopLoader();
					console.log(res);
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${ScreeningComponent.name}][${this.saveVaccinations.name}]`, 'Error occurred while saving vaccinations ', err);
				}
			);
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
